import { apiListQuery, apiQuery, createQueryKeys, useApiMutation } from '@/shared/data';
import { getCurrentTenant } from '@/shared/http/client';
import { listLegalPersonOptions, listPersonOptions } from './person.api';
import type { BankAccountBody } from './person-bank-account';
import type { FraudRecordBody } from './person-fraud.form';
import type { ComplianceChecklistCreate } from './person.wire';
const keys = createQueryKeys('person');
/** Chaves de cache do módulo; `personKeys.all` limpa tudo de pessoa. */
export const personKeys = keys;
interface QueryExtra {
  enabled?: boolean;
}
function resolveTenant<T extends { tenant?: string | undefined }>(params: T) {
  return { ...params, tenant: 'tenant' in params ? params.tenant : getCurrentTenant() };
}
export function legalPersonOptionsQuery(
  params: { searchString?: string | undefined; tenant?: string | undefined } = {},
  extra: QueryExtra = {},
) {
  const resolved = resolveTenant(params);
  return apiListQuery(
    keys.custom('options', 'legal-person', resolved),
    ({ signal }) => listLegalPersonOptions(resolved, signal),
    extra,
  );
}
export function personOptionsQuery(
  params: { searchString?: string | undefined; tenant?: string | undefined } = {},
) {
  const resolved = resolveTenant(params);
  return apiListQuery(keys.custom('options', 'person', resolved), ({ signal }) =>
    listPersonOptions(resolved, signal),
  );
}
export function naturalPersonOptionsQuery(
  params: { tenant?: string | undefined; searchString?: string | undefined } = {},
  extra: { enabled?: boolean } = {},
) {
  const resolved = resolveTenant(params);
  return apiListQuery(
    keys.custom('options', 'natural-person', resolved),
    async ({ signal }) => (await import('./person.api')).listNaturalPersonOptions(resolved, signal),
    extra,
  );
}

// ─── Listas e mutações da tela (X06, X07) ──────────────────────────────────

/** Mutações do módulo por nome (X07). O feedback é da tela (X06): o hook só expõe a mutação. */
const api = () => import('./person.api');
const MUTATIONS = {
  saveNaturalPerson: async (input: { id: string | undefined; body: Readonly<Record<string, unknown>>; tenant: string | undefined }) => (await api()).saveNaturalPerson(input),
  saveLegalPerson: async (input: { id: string | undefined; body: Readonly<Record<string, unknown>>; tenant: string | undefined }) => (await api()).saveLegalPerson(input),
  clone: async (input: { resource: 'NaturalPerson' | 'LegalPerson'; body: Readonly<Record<string, unknown>>; tenant: string }) => (await api()).clonePerson(input.resource, input.body, input.tenant),
  /** Consulta paga ao bureau: só pela ação com prévia da tela (X01). */
  requestDataset: async (input: { resource: 'NaturalPerson' | 'LegalPerson'; id: string; dataset: string }) => (await api()).requestPersonDataset(input.resource, input.id, input.dataset),
  assign: async (input: { resource: 'NaturalPerson' | 'LegalPerson'; id: string; userId: string | null; groupName: string | null; tenant: string }) =>
    (await api()).assignPerson(input.resource, input.id, input),
  /** "Regularizar CPF" (só PF). */
  regulate: async (input: { id: string }) => (await api()).regulateRegistrationNumber(input.id),
  /** "Carregar informações": consulta ao bureau, só pelo formulário com o documento (X01). */
  queryBasicData: async (input: { registrationNumber: string }) => (await api()).queryBasicData(input.registrationNumber),
  /** "Incluir na Blacklist". */
  createComplianceChecklist: async (input: { items: readonly ComplianceChecklistCreate[] }) => (await api()).createComplianceChecklist(input.items),
  /** "Registrar fraude" (dado de PLD): só pelo formulário de três passos, com prévia (unidade 8X). */
  registerFraud: async (input: { personId: string; body: FraudRecordBody }) => (await api()).registerFraud(input.personId, input.body),
  /** Dados bancários (10E): conta nova (`POST`, devolve os ids) ou a conta escolhida (`PUT`); as outras ficam como estão. */
  createBankAccount: async (input: { resource: 'NaturalPerson' | 'LegalPerson'; personId: string; body: BankAccountBody }) => (await api()).createPersonBankAccount(input.resource, input.personId, input.body),
  updateBankAccount: async (input: { resource: 'NaturalPerson' | 'LegalPerson'; personId: string; accountId: string; body: BankAccountBody }) =>
    (await api()).updatePersonBankAccount(input.resource, input.personId, input.accountId, input.body),
  /** "Reservar número de conta" (só PF): abre a conta transitória da pessoa (OpenBankAccount.tsx). */
  openBankAccount: async (input: { personId: string }) => (await api()).openNaturalPersonBankAccount(input.personId),
} as const;
export type PersonMutationName = keyof typeof MUTATIONS;
type MutationInput<N extends PersonMutationName> = Parameters<(typeof MUTATIONS)[N]>[0];
type MutationOutput<N extends PersonMutationName> = Awaited<ReturnType<(typeof MUTATIONS)[N]>>;

/** Um hook genérico por módulo, tipado pelo nome (X07); padrão sem toast (X06, S11). */
export function usePersonMutation<N extends PersonMutationName>(name: N, options: { success?: false; error?: false } = {}) {
  return useApiMutation<MutationOutput<N>, MutationInput<N>>({
    mutationFn: (input) => (MUTATIONS[name] as (value: MutationInput<N>) => Promise<MutationOutput<N>>)(input),
    success: options.success ?? false,
    error: options.error ?? false,
    invalidate: [keys.all],
  });
}

/**
 * "Excluir" de conta gravada (R5): `DELETE` da conta, **sem invalidar nada**, como o `useDeleteBankAccountMutation`
 * (personsContext.tsx:75-113); a lista sai do formulário depois do sucesso. Sem feedback próprio (X06).
 */
export function useDeleteBankAccountMutation() {
  return useApiMutation<unknown, { resource: 'NaturalPerson' | 'LegalPerson'; personId: string; accountId: string }>({
    mutationFn: async (input) => (await api()).deletePersonBankAccount(input.resource, input.personId, input.accountId),
    success: false,
    error: false,
  });
}

/** `GET /NaturalPerson/{id}` (`useNaturalPersonData`, naturalPersonContext.tsx:32-76). */
export function naturalPersonDetailQuery(id: string) {
  return apiQuery(keys.detail(`natural:${id}`), async ({ signal }) => (await api()).getNaturalPerson(id, signal));
}

/** `GET /LegalPerson/{id}` (`useLegalPersonData`, legalPersonContext.tsx:20-59). */
export function legalPersonDetailQuery(id: string, extra: { enabled?: boolean } = {}) {
  return apiQuery(keys.detail(`legal:${id}`), async ({ signal }) => (await api()).getLegalPerson(id, signal), extra);
}

export const personDatasetKey = (resource: string, id: string, dataset: string) => keys.custom('dataset', resource, id, dataset);

/** `useDatasetData` (datasetContext.tsx:56-126): 2 min de cache; nunca no cadastro novo. */
export function personDatasetQuery(resource: 'NaturalPerson' | 'LegalPerson', id: string, dataset: string, enabled = true, params?: Readonly<Record<string, string | number | boolean>>) {
  return apiQuery(personDatasetKey(resource, id, dataset), async ({ signal }) => (await api()).getPersonDataset(resource, id, dataset, signal, params), {
    staleTime: 120_000,
    enabled: enabled && id !== '' && id !== 'nova' && id !== 'novo',
  });
}

/** Tokens de averbação (`useGetTokenRegisteredByPersonId`, naturalPersonContext.tsx:171-205). */
export function reserveTokenDatasetQuery(personId: string, enabled = true) {
  return apiQuery(keys.custom('reserve-token-dataset', personId), async ({ signal }) => (await api()).listReserveTokenDataset(personId, signal), { enabled: enabled && personId !== '' });
}
